/**
 * Authentication Context
 * Provides user authentication state and methods throughout the app
 */

import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import {
  type User,
  signInWithPopup,
  signInWithRedirect,
  getRedirectResult,
  signOut as firebaseSignOut,
  onAuthStateChanged,
} from 'firebase/auth';
import { Capacitor } from '@capacitor/core';
import { FirebaseAuthentication } from '@capacitor-firebase/authentication';
import { GoogleAuthProvider, signInWithCredential } from 'firebase/auth';
import { auth, googleProvider } from '@/lib/firebase';
import { createOrUpdateUserProfile } from '@/services/user.service';
import { SIGN_IN_FLAG } from '@/components/SignInMask';

interface AuthContextType {
  user: User | null;
  loading: boolean;
  signInWithGoogle: () => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let unsubscribe: (() => void) | null = null;

    // Wait for redirect result first so loading stays true until auth is fully resolved.
    // This prevents the login page from flashing before the user state is known.
    getRedirectResult(auth)
      .catch((err) => {
        console.error('getRedirectResult error:', err?.code, err?.message);
      })
      .finally(() => {
        unsubscribe = onAuthStateChanged(auth, async (user) => {
          sessionStorage.removeItem(SIGN_IN_FLAG);
          if (user) {
            try {
              await createOrUpdateUserProfile(
                user.uid,
                user.email || '',
                user.displayName,
                user.photoURL
              );
            } catch (error) {
              console.error('Error creating user profile:', error);
            }
          }
          setUser(user);
          setLoading(false);
        });
      });

    return () => { if (unsubscribe) unsubscribe(); };
  }, []);

  const signInWithGoogle = async () => {
    setLoading(true);

    // Capacitor native: signInWithRedirect/Popup don't work in WebView (the
    // redirect-back URL is https://localhost which Chrome can't reach). Use
    // the native Google Sign-In SDK to get an ID token, then exchange it for
    // a Firebase credential.
    if (Capacitor.isNativePlatform()) {
      try {
        const result = await FirebaseAuthentication.signInWithGoogle();
        const idToken = result.credential?.idToken;
        if (!idToken) throw new Error('Native Google Sign-In returned no idToken');
        const credential = GoogleAuthProvider.credential(idToken);
        await signInWithCredential(auth, credential);
      } catch (error) {
        console.error('Native Google Sign-In failed:', error);
        setLoading(false);
        throw error;
      }
      return;
    }

    // Web: try popup first (better UX), fall back to redirect if blocked.
    try {
      await signInWithPopup(auth, googleProvider);
    } catch (error: any) {
      if (error?.code === 'auth/popup-blocked') {
        sessionStorage.setItem(SIGN_IN_FLAG, Date.now().toString());
        await signInWithRedirect(auth, googleProvider);
        return;
      }
      console.error('Error signing in with Google:', error);
      setLoading(false);
      throw error;
    }
  };

  const signOut = async () => {
    try {
      setLoading(true);
      if (Capacitor.isNativePlatform()) {
        await FirebaseAuthentication.signOut();
      }
      await firebaseSignOut(auth);
    } catch (error) {
      console.error('Error signing out:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const value = {
    user,
    loading,
    signInWithGoogle,
    signOut,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
